/**
 * PII Masking Utility for Frontend Logs and RUM Events
 *
 * Recursively masks personally identifiable information (PII) in objects
 * before they are sent to Datadog (both browser-logs and RUM).
 *
 * This is the frontend counterpart of functions/utils/maskPII.js.
 * Both files use the same masking logic to ensure consistent PII handling
 * across the entire stack.
 *
 * What gets masked:
 *   - Email addresses: j*****e@gmail.com (keeps first/last char + domain)
 *   - Phone numbers: ******7890 (keeps last 4 digits, preserves formatting)
 *
 * Used in:
 *   - src/lib/datadog.js → beforeSend callbacks for both Logs and RUM
 */

import { scrubSensitiveUrl } from '@/lib/scrub-sensitive-data';

const MAX_RECURSION_DEPTH = 4;
const EMAIL_LOCAL_CHARS = new Set("ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.!#$%&'*+/=?^_`{|}~-");
const EMAIL_DOMAIN_CHARS = new Set('ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789.-');

const isEmailCandidate = (value) => {
  const atIndex = value.indexOf('@');
  const dotAfterAt = value.indexOf('.', atIndex + 2);
  return atIndex > 0 && dotAfterAt > atIndex && dotAfterAt < value.length - 1 && !value.includes('..');
};

// Masks an email. Keeps the domain name and first and last character of the username.
// eg johndoe@gmail.com -> j*****e@gmail.com
const maskEmail = (email) => {
  if (!email || typeof email !== 'string') {
    return email;
  }

  const emailString = String(email).trim();

  if (!isEmailCandidate(emailString)) {
    return emailString;
  }

  const atIndex = emailString.indexOf('@');
  const username = emailString.slice(0, Math.max(0, atIndex));
  const domain = emailString.slice(Math.max(0, atIndex));

  if (username.length <= 2) {
    return emailString;
  }

  const firstChar = username[0];
  const lastChar = username[username.length - 1];
  const maskedMiddle = '*'.repeat(username.length - 2);

  return firstChar + maskedMiddle + lastChar + domain;
};

const maskPIIinString = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  value = scrubSensitiveUrl(value);

  let maskedValue = '';
  let cursor = 0;

  while (cursor < value.length) {
    const atIndex = value.indexOf('@', cursor);
    if (atIndex === -1) {
      maskedValue += value.slice(cursor);
      break;
    }

    let start = atIndex - 1;
    while (start >= cursor && EMAIL_LOCAL_CHARS.has(value[start])) {
      start--;
    }
    start++;

    let end = atIndex + 1;
    while (end < value.length && EMAIL_DOMAIN_CHARS.has(value[end])) {
      end++;
    }

    const candidate = value.slice(start, end);
    maskedValue += value.slice(cursor, start);
    maskedValue += isEmailCandidate(candidate) ? maskEmail(candidate) : candidate;
    cursor = end;
  }

  return maskedValue;
};

// Masks phone numbers by keeping only the last 4 digits and preserving formatting
// eg. 1234567890 -> ******7890
const maskPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) {
    return phoneNumber;
  }

  const phoneString = String(phoneNumber);

  const digits = phoneString.match(/\d/g);
  if (!digits || digits.length < 4) {
    // If less than 4 digits, mask all digits
    return phoneString.replaceAll(/\d/g, '*');
  }

  // Keep track of how many digits we've seen
  let digitCount = 0;
  const totalDigits = digits.length;

  // Replace each digit, keeping only the last 4
  return phoneString.replaceAll(/\d/g, (digit) => {
    digitCount++;
    // Keep the last 4 digits, mask the rest
    return digitCount <= totalDigits - 4 ? '*' : digit;
  });
};

// Helper function to check if a property key indicates it contains a phone number
const isPhoneNumberKey = (key) => {
  if (!key || typeof key !== 'string') {
    return false;
  }
  const lowerKey = key.toLowerCase();
  return lowerKey === 'phone_text' || lowerKey === 'to_number' || lowerKey === 'from_number' || lowerKey.includes('phone');
};

const maskObjectValue = (key, value, depth, seen) => {
  const shouldMaskPhone = (typeof value === 'string' || typeof value === 'number') && isPhoneNumberKey(key);
  return shouldMaskPhone ? maskPhoneNumber(value) : maskPIIinObject(value, depth + 1, seen);
};

const maskPlainObject = (obj, depth, seen) => {
  if (seen.has(obj)) {
    return '[Circular]';
  }
  seen.add(obj);

  const maskedObject = {};

  // Copy Symbol properties (important for Winston and other libraries)
  for (const sym of Object.getOwnPropertySymbols(obj)) {
    maskedObject[sym] = obj[sym];
  }

  for (const key of Object.keys(obj)) {
    maskedObject[key] = maskObjectValue(key, obj[key], depth, seen);
  }

  return maskedObject;
};

/**
 * Recursively masks PII in an object.
 * Safe to call on any value — returns the original if masking fails.
 *
 * @param {*} obj - The value to mask (object, array, string, etc.)
 * @param {number} depth - Current recursion depth (internal use)
 * @returns {*} A new object with PII masked, or the original on error
 */
const maskPIIinObject = (obj, depth = 0, seen = new WeakSet()) => {
  try {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Stop recursion if we're too deep. Safety measure to prevent infinite recursion.
    if (depth >= MAX_RECURSION_DEPTH) {
      return obj;
    }

    const t = typeof obj;

    if (t !== 'object') {
      // this is the case for string, number, boolean, function etc.
      return t === 'string' ? maskPIIinString(obj) : obj;
    }

    if (obj instanceof Date || (typeof Buffer !== 'undefined' && Buffer.isBuffer && Buffer.isBuffer(obj))) {
      return obj;
    }

    if (Array.isArray(obj)) {
      if (seen.has(obj)) {
        return '[Circular]';
      }
      seen.add(obj);
      return obj.map((item) => maskPIIinObject(item, depth + 1, seen));
    }

    if (obj instanceof Error) {
      return {
        name: obj.name,
        message: maskPIIinString(obj.message),
        stack: maskPIIinString(obj.stack || ''),
      };
    }

    return maskPlainObject(obj, depth, seen);
  } catch (error) {
    // Return original object if anything fails. We dont want the app to crash when logging!
    console.error('Error in maskPIIinObject:', error);
    return obj;
  }
};

export { maskPIIinObject, maskPIIinString };
